import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from './components/common/ScrollToTop';
import { RouteErrorBoundary } from './components/common/ErrorBoundary';
import SignInPage from "./pages/SignInPage";
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
import { AuthProvider, sessionLanding, useAuth } from "./authContext";
import { SurveyProvider } from "./surveyContext";
import { CompanyProvider } from "./services/CompanyContext";
import { UserProvider } from "./services/UserContext";
import ProtectedLayout from "./components/Base/ProtectedLayout";
import SessionGuard from "./components/Base/SessionGuard";
import GodModeBanner from "./components/GodMode/GodModeBanner";

// Audit F-02 — code-splitting par route : chaque page part dans son propre
// chunk au lieu d'un bundle monolithique (recharts, wizard, analytics Pro...).
// SignInPage et ProtectedLayout restent eager (premiere peinture).
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AccountBlocked = lazy(() => import("./pages/AccountBlocked"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ManagerResults = lazy(() => import("./pages/ManagerResults"));
const ResetPassword = lazy(() => import("./components/Auth/ResetPassword"));
const ChangePassword = lazy(() => import("./components/Auth/ChangePassword"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Employees = lazy(() => import("./pages/Employees"));
const Help = lazy(() => import("./pages/Help"));
const MySurveys = lazy(() => import("./pages/MySurveys"));
const Modeles = lazy(() => import("./pages/Modeles"));
const EnqueteWizard = lazy(() => import("./pages/EnqueteWizard"));
const Settings = lazy(() => import("./pages/Settings"));
const Synthesis = lazy(() => import("./pages/Synthesis"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const ServeyQuestions = lazy(() => import("./components/servey-questions/ServeyQuestions"));
const SurveyQuestionPage = lazy(() => import("./pages/SurveyQuestionPage"));
const SurveyEnd = lazy(() => import("./pages/SurveyEnd"));
const ResultsLayout = lazy(() => import("./components/Analytics/ResultsLayout"));
const AnalysisPage = lazy(() => import("./components/Analytics/AnalysisPage"));
const InsightsPage = lazy(() => import("./components/Analytics/InsightsPage"));
const DashboardOverview = lazy(() => import("./components/Analytics/DashboardOverview"));
const RiskAlerts = lazy(() => import("./components/Analytics/RiskAlerts"));
const ActionPlanPage = lazy(() => import("./components/ActionPlan/ActionPlanPage"));
const BenchmarkDashboard = lazy(() => import("./components/Analytics/BenchmarkDashboard"));
const TeamsPage = lazy(() => import("./pages/placeholders/TeamsPage"));
const ManagersPage = lazy(() => import("./pages/placeholders/ManagersPage"));
const SettingsBilling = lazy(() => import("./pages/placeholders/SettingsBilling"));
const SettingsUsers = lazy(() => import("./pages/SettingsUsers"));
const DevComponents = lazy(() => import("./pages/DevComponents"));

// Back-office plateforme (/platform/*) : monde d'identité SÉPARÉ des comptes
// clients (jeton propre, en sessionStorage, instance axios dédiée). Les chemins
// d'import des deux pages de liste doivent rester identiques à routePrefetch.js.
const PlatformGuard = lazy(() => import("./components/Platform/PlatformGuard"));
const PlatformLayout = lazy(() => import("./components/Platform/PlatformLayout"));
const PlatformSignIn = lazy(() => import("./pages/platform/PlatformSignIn"));
const PlatformPassword = lazy(() => import("./pages/platform/PlatformPassword"));
const PlatformCompanies = lazy(() => import("./pages/platform/PlatformCompanies"));
const PlatformCompanyNew = lazy(() => import("./pages/platform/PlatformCompanyNew"));
const PlatformCompanyDetail = lazy(() => import("./pages/platform/PlatformCompanyDetail"));
const PlatformAudit = lazy(() => import("./pages/platform/PlatformAudit"));

const SuspenseFallback = (
  <div className="flex justify-center items-center min-h-screen">
    <span className="loading loading-spinner loading-lg text-bsrate"></span>
  </div>
);

const LandingRedirect = () => {
  const { authData } = useAuth();
  return <SessionGuard>{authData && <Navigate to={sessionLanding(authData.user)} replace />}</SessionGuard>;
};

const App = () => {
  return (
    <AuthProvider>
      <SurveyProvider>
        <CompanyProvider>
          <UserProvider>
            <BrowserRouter future={{ v7_startTransition: true }}>
              <ScrollToTop />
              {/* Mode intervention : bandeau permanent au-dessus de TOUT le layout client. */}
              <GodModeBanner />
              <Suspense fallback={SuspenseFallback}>
              {/* Filet exterieur : couvre aussi les ecrans publics, a commencer
                  par le questionnaire vu par les salaries. La frontiere interne
                  (ProtectedLayout) attrape avant lui et preserve la navigation.
                  Ici c'est `Routes` lui-meme qui a disparu : plus un seul lien a
                  cliquer, donc ni « allez dans le menu » ni un simple nouveau
                  rendu — le seul recours honnete est de recharger. */}
              <RouteErrorBoundary
                message="Un incident a interrompu l'affichage. Rechargez la page pour reprendre."
                onRetry={() => window.location.reload()}
              >
              <Routes>
                {/* Public routes */}
                <Route path="/signin" element={<SignInPage />} />
                <Route path="/signup" element={<SessionGuard publicAdmin><SignUpPage /></SessionGuard>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/admin/reset-new-password/:token"
                  element={<ChangePassword />}
                />
                <Route path="/accept-invite/:token" element={<AcceptInvite />} />
                <Route path="/onboarding" element={<SessionGuard publicAdmin><Onboarding /></SessionGuard>} />

                {/* Survey response routes (public, employee-facing) */}
                <Route
                  path="/surveys/:adminId/:surveyId/:surveySendId/:employeeId/:token"
                  element={<SurveyQuestionPage />}
                />
                <Route
                  path="/surveys/:surveyId/:surveySendId/:employeeId/:token"
                  element={<ServeyQuestions />}
                />
                <Route path="/survey-end" element={<SurveyEnd />} />
                {/* Publiques : l'ecran vu par les salaries repondants doit y mener. */}
                <Route path="/mentions-legales" element={<LegalNotice />} />
                <Route path="/confidentialite" element={<PrivacyPolicy />} />

                {/* Entreprise suspendue ou clôturée : écran dédié (SPEC §3.1). */}
                <Route path="/compte-suspendu" element={<SessionGuard allowBlocked><AccountBlocked /></SessionGuard>} />

                {/* Back-office plateforme — hors ProtectedLayout et hors SessionGuard
                    (aucun contexte client). La connexion est publique ; tout le
                    reste passe par PlatformGuard (jeton plateforme vérifié). */}
                <Route path="/platform/signin" element={<PlatformSignIn />} />
                <Route path="/platform" element={<PlatformGuard />}>
                  <Route index element={<Navigate to="companies" replace />} />
                  <Route path="password" element={<PlatformPassword />} />
                  <Route element={<PlatformLayout />}>
                    <Route path="companies" element={<PlatformCompanies />} />
                    <Route path="companies/new" element={<PlatformCompanyNew />} />
                    <Route path="companies/:id" element={<PlatformCompanyDetail />} />
                    <Route path="audit" element={<PlatformAudit />} />
                  </Route>
                </Route>

                {/* Protected routes — ProtectedLayout includes Sidebar3 + AnalyticsProvider */}
                <Route element={<ProtectedLayout />}>
                  <Route path="/manager/results" element={<ManagerResults />} />
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* Enquetes */}
                  <Route path="/surveys/mine" element={<MySurveys />} />
                  <Route path="/surveys/create" element={<EnqueteWizard />} />
                  <Route path="/surveys/create/:surveyId" element={<EnqueteWizard />} />
                  <Route path="/surveys/templates" element={<Modeles />} />

                  {/* Resultats */}
                  <Route path="/results" element={<ResultsLayout />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<DashboardOverview />} />
                    <Route path="analysis" element={<AnalysisPage />} />
                    <Route path="insights" element={<InsightsPage />} />
                    <Route path="critical-points" element={<RiskAlerts />} />
                    <Route path="action-plan" element={<ActionPlanPage />} />
                  </Route>

                  {/* Organisation */}
                  <Route path="/organization/employees" element={<Employees />} />
                  <Route path="/organization/teams" element={<TeamsPage />} />
                  <Route path="/organization/managers" element={<ManagersPage />} />

                  {/* Benchmark */}
                  <Route path="/benchmark" element={<BenchmarkDashboard />} />

                  {/* Parametres */}
                  <Route path="/settings/account" element={<MyAccount />} />
                  <Route path="/settings/company" element={<Settings />} />
                  <Route path="/settings/billing" element={<SettingsBilling />} />
                  <Route path="/settings/users" element={<SettingsUsers />} />

                  {/* Pages conservees */}
                  <Route path="/synthesis" element={<Synthesis />} />
                  <Route path="/help" element={<Help />} />
                </Route>

                {/* Dev only — playground composants partages (Brief 040). Jamais en prod. */}
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/dev/components" element={<DevComponents />} />
                )}

                {/* Legacy redirects */}
                <Route path="/" element={<LandingRedirect />} />
                <Route path="/home" element={<LandingRedirect />} />
                <Route path="/analytics" element={<Navigate to="/results/overview" replace />} />
                <Route path="/analytics/overview" element={<Navigate to="/results/overview" replace />} />
                <Route path="/analytics/segments" element={<Navigate to="/results/analysis" replace />} />
                <Route path="/analytics/managers" element={<Navigate to="/results/analysis" replace />} />
                <Route path="/analytics/evolution" element={<Navigate to="/results/analysis" replace />} />
                <Route path="/analytics/verbatims" element={<Navigate to="/results/insights" replace />} />
                <Route path="/analytics/drivers" element={<Navigate to="/results/insights" replace />} />
                <Route path="/analytics/risks" element={<Navigate to="/results/critical-points" replace />} />
                <Route path="/analytics/recommendations" element={<Navigate to="/results/action-plan" replace />} />
                <Route path="/analytics/benchmarks" element={<Navigate to="/benchmark" replace />} />
                <Route path="/analytics/journey" element={<Navigate to="/results/analysis" replace />} />
                <Route path="/analytics/cross" element={<Navigate to="/results/analysis" replace />} />
                <Route path="/employees" element={<Navigate to="/organization/employees" replace />} />
                <Route path="/settings" element={<Navigate to="/settings/company" replace />} />
                <Route path="/surveys" element={<Navigate to="/surveys/mine" replace />} />
                <Route path="/new-survey" element={<Navigate to="/surveys/create" replace />} />
                <Route path="/my-account" element={<Navigate to="/settings/account" replace />} />
                {/* `/login` n'a jamais été une route de l'app, mais c'est l'URL
                    remise aux comptes Custom avec leur mot de passe temporaire
                    (`controllers/adminCustom.js`, documentée dans
                    ADMIN_OPERATIONS.md). Elle ne « marchait » que par accident,
                    via le fourre-tout qui renvoyait tout sur /dashboard. La 404
                    la casserait pour tous les liens déjà distribués : on garde
                    donc l'alias explicite. */}
                <Route path="/login" element={<Navigate to="/signin" replace />} />
                {/* 20/08/2026 — une adresse inconnue redirigeait EN SILENCE vers
                    le tableau de bord : un lien mort ou une coquille menaient à
                    un écran normal, sans signal, et un lien cassé partagé avait
                    l'air de fonctionner. Les redirections d'URL historiques
                    ci-dessus restent explicites ; le reste est une vraie 404. */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </RouteErrorBoundary>
              </Suspense>
            </BrowserRouter>
          </UserProvider>
        </CompanyProvider>
      </SurveyProvider>
    </AuthProvider>
  );
};

export default App;
